import {noop} from 'lodash';
import React, {FC} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {PredicateOf} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {InboxModel} from '../../../../../libs/shared/core/src/models/api/inboxModel';
import {TeammateModel} from '../../../../../libs/shared/core/src/models/api/teammateModel';
import {usePush} from '../../../../../libs/shared/core/src/services/push/pushContext';
import {InboxesPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {CellsConversationsDragItem} from '../../../../helpers/conversationCellHelpers';
import {
  discussionsCounterKey,
  selfAssignedCounterKey,
  selfInboxCounterKey,
  sharedWithMeCounterKey,
} from '../../../../helpers/counterHelpers';
import {buildTeammatePath} from '../../../../helpers/modelHelpers';
import {DelegationModel} from '../../../../models/api/delegationModel';
import {CountersVisibilityModesEnum} from '../../../../models/api/preferencesModel';
import {InboxesFiltersEnum, TeammatesFoldersEnum} from '../../../../models/url/models/inboxesUrlModel';
import {TeammateOnlineStatusesEnum} from '../../../../store/data/teammates/onlineTeammatesSelectors';
import {TeammateWorkspaceStarItemContainer} from '../teammateWorkspace/teammateWorkspaceStarItemContainer';
import {WorkspaceGenericItemContainer} from '../workspaceGenericItemContainer';
import {WorkspaceTeammateItemContainerBase} from '../workspaceTeammateItemContainerBase';
import {DelegationWorkspaceInboxes} from './delegationWorkspaceInboxes';

/*
 * Props.
 */

interface DelegationWorkspaceInboxTagProps {
  panelId: InboxesPanelId;
  delegation: DelegationModel;
  owner: TeammateModel;
  ownerInboxes: ReadonlyArray<InboxModel>;
  ownerStarredTagId?: number;
  ownerOnlineStatus: TeammateOnlineStatusesEnum;
  isExpanded: boolean;
  hasInternalDiscussions: boolean;
  onToggleExpandClick: () => void;
  canConversationsDrop: PredicateOf<CellsConversationsDragItem>;
  onConversationsDrop: (item: CellsConversationsDragItem) => void;
}

/*
 * Component.
 */

export const DelegationWorkspaceInboxTag: FC<DelegationWorkspaceInboxTagProps> = (props) => {
  const {isConnected} = usePush();
  const isOffline = !isConnected;

  return (
    <>
      {/* Aggregate view. */}
      <WorkspaceTeammateItemContainerBase
        panelId={props.panelId}
        teammate={props.owner}
        onlineStatus={props.ownerOnlineStatus}
        to={buildTeammatePath(props.owner.id, TeammatesFoldersEnum.INBOX, InboxesFiltersEnum.OPEN)}
        counterKey={selfInboxCounterKey}
        counterDelegationId={props.delegation.id}
        countersVisibility={CountersVisibilityModesEnum.OPEN}
        isExpanded={props.isExpanded}
        onToggleExpandClick={props.onToggleExpandClick}
        canConversationsDrop={props.canConversationsDrop}
        onConversationsDrop={props.onConversationsDrop}
        isDisabled={isOffline}
        shouldHideMoreMenu
        canBeHidden={false}
        shouldAlphabetizeWorkspace={false}
        isEditingWorkspace={false}
        workspaceDragItem={undefined}
        onDropReorderTeammate={noop}
      >
        <Trans id="workspace-inbox">Inbox</Trans>
      </WorkspaceTeammateItemContainerBase>

      {/* Content of the "Inbox" section. */}
      {maybeRenderExpandedContent(props, isOffline)}
    </>
  );
};

function maybeRenderExpandedContent(props: DelegationWorkspaceInboxTagProps, isOffline: boolean) {
  const {panelId, isExpanded, delegation, owner, ownerInboxes, hasInternalDiscussions, ownerStarredTagId} =
    props;
  if (!isExpanded) {
    return null;
  }

  // Render the exact same folders as the owner's Inbox.
  return (
    <>
      {/* Assigned to <owner>. */}
      <WorkspaceGenericItemContainer
        shouldHideMoreMenu
        canBeHidden={false}
        shouldAlphabetizeWorkspace={false}
        workspaceDragItem={undefined}
        panelId={panelId}
        to={buildTeammatePath(owner.id, TeammatesFoldersEnum.ASSIGNED, InboxesFiltersEnum.OPEN)}
        iconName="assignSmall"
        selectedIconName="assignFilledSmall"
        counterKey={selfAssignedCounterKey}
        counterDelegationId={delegation.id}
        countersVisibility={CountersVisibilityModesEnum.OPEN}
        indentLevel={1}
        isDisabled={isOffline}
      >
        <Trans id="workspace-assignedtoowner">Assigned to {owner.givenName}</Trans>
      </WorkspaceGenericItemContainer>

      {/* Shared with <owner>. */}
      <WorkspaceGenericItemContainer
        shouldHideMoreMenu
        canBeHidden={false}
        shouldAlphabetizeWorkspace={false}
        workspaceDragItem={undefined}
        panelId={panelId}
        to={buildTeammatePath(owner.id, TeammatesFoldersEnum.SHARED_WITH_ME, InboxesFiltersEnum.OPEN)}
        iconName="teammatesSmall"
        selectedIconName="teammatesFilledSmall"
        counterKey={sharedWithMeCounterKey}
        counterDelegationId={delegation.id}
        countersVisibility={CountersVisibilityModesEnum.OPEN}
        indentLevel={1}
        isDisabled={isOffline}
      >
        <Trans id="workspace-sharedwithowner">Shared with {owner.givenName}</Trans>
      </WorkspaceGenericItemContainer>

      {/* Discussions. */}
      {hasInternalDiscussions && (
        <WorkspaceGenericItemContainer
          shouldHideMoreMenu
          canBeHidden={false}
          shouldAlphabetizeWorkspace={false}
          workspaceDragItem={undefined}
          panelId={panelId}
          to={buildTeammatePath(owner.id, TeammatesFoldersEnum.DISCUSSIONS, InboxesFiltersEnum.OPEN)}
          iconName="discussionSmall"
          selectedIconName="discussionFilledSmall"
          counterKey={discussionsCounterKey}
          counterDelegationId={delegation.id}
          countersVisibility={CountersVisibilityModesEnum.OPEN}
          indentLevel={1}
          isDisabled={isOffline}
        >
          <Trans id="workspace-discussions">Discussions</Trans>
        </WorkspaceGenericItemContainer>
      )}

      {/* Inboxes. */}
      <DelegationWorkspaceInboxes
        panelId={panelId}
        delegation={delegation}
        inboxes={ownerInboxes}
        indentLevel={1}
        isDisabled={isOffline}
      />

      {!!ownerStarredTagId && (
        <TeammateWorkspaceStarItemContainer
          shouldHideMoreMenu
          canBeHidden={false}
          panelId={panelId}
          namespace={owner.namespace}
          starredTagId={ownerStarredTagId}
        />
      )}
    </>
  );
}
